import React, { useRef } from 'react';

import { Avatar } from '@change/ds-core-ui/Avatar';
import { Card, CardBody, CardFooter } from '@change/ds-core-ui/Card';
import { Link } from '@change/ds-core-ui/Link';
import { Text } from '@change/ds-core-ui/Text';

import { parseHtml } from '@change-corgi/core-react/html';
import { Translate, TranslateHtml } from '@change-corgi/core-react/i18n';

import { useResponseListCard } from './hooks/useResponseListCard';

export type ResponseListCardProps = {
	responseId: string;
	message: string;
	petitionHref: string;
	petitionAsk: string;
	petitionId: string;
};

export const ResponseListCard = ({
	ref,
	responseId,
	message,
	petitionHref,
	petitionAsk,
	petitionId,
}: ResponseListCardProps & {
	ref?: React.RefObject<HTMLDivElement | null>;
}) => {
	const {
		data: { displayName, responseText, avatarUrl, showReadMoreLink },
		actions: { onClickPetitionLink, onClickReadMoreLink },
	} = useResponseListCard(responseId, petitionId, message);
	const responseTextRef = useRef<HTMLDivElement>(null);

	return (
		<Card data-qa="dm-responses-card" ref={ref} className="flex h-full flex-col">
			<CardBody>
				<div className="mb-4 flex items-center gap-2">
					<Avatar alt={`${displayName} avatar`} size={80} src={avatarUrl} />
					<Text variant="body">
						<TranslateHtml
							value="corgi.decision_maker_profile.response_card.header"
							replacements={{
								displayName,
								petitionLink: (
									<Link
										href={petitionHref}
										className="inline"
										onClick={() => onClickPetitionLink()}
										data-qa="dm-response-card-petition-link"
									>
										{petitionAsk}
									</Link>
								),
							}}
						/>
					</Text>
				</div>
				<div className="border-stroke-weak max-h-[250px] flex-1 overflow-y-auto border-l-2 pl-4">
					<Text as="p" variant="body" tabIndex={0} ref={responseTextRef}>
						“{parseHtml(responseText)}”
					</Text>
				</div>
			</CardBody>
			<CardFooter>
				{showReadMoreLink && (
					<Link
						as="button"
						variant="secondary"
						onClick={() => onClickReadMoreLink(responseTextRef)}
						data-qa="dm-response-read-more"
					>
						<Translate value="corgi.decision_maker_profile.response_card.link" />
					</Link>
				)}
			</CardFooter>
		</Card>
	);
};

ResponseListCard.displayName = 'ResponseListCard';
